interface ExportData {
  employeeName: string
  employeeId: string
  employeeEmail: string
  department: string
  month: string
  attendance: any[]
  dailyWork: any[]
  tasks: any[]
}

const escapeHtml = (value: unknown) => String(value ?? '-').replace(/[&<>'"]/g, char => ({
  '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;',
}[char] || char))

const formatDate = (date: string) => {
  const [year, month, day] = date.split('-')
  return year && month && day ? `${day}/${month}/${year}` : date || '-'
}

const formatTime = (time: string | null) => {
  if (!time || !/^\d{2}:\d{2}$/.test(time)) return '-'
  const [hour, minute] = time.split(':').map(Number)
  return `${hour % 12 || 12}:${String(minute).padStart(2, '0')} ${hour >= 12 ? 'PM' : 'AM'}`
}

export function exportMonthlyReport(data: ExportData) {
  const attendance = data.attendance
    .filter(record => record.date?.startsWith(data.month))
    .sort((a, b) => a.date.localeCompare(b.date))
  const [year, monthNumber] = data.month.split('-')
  const monthDate = new Date(Number(year), Number(monthNumber) - 1, 1)
  const monthLabel = Number.isNaN(monthDate.getTime())
    ? data.month
    : monthDate.toLocaleString('en-US', { month: 'long', year: 'numeric' })
  const presentDays = attendance.filter(record => record.checkIn).length
  const completedDays = attendance.filter(record => record.checkIn && record.checkOut).length
  const incompleteDays = Math.max(0, presentDays - completedDays)

  const rows = attendance.length === 0
    ? `<tr><td colspan="6" class="empty">No attendance records were available for this reporting period.</td></tr>`
    : attendance.map(record => {
      const dayLog = data.dailyWork
        .filter(log => log.date === record.date)
        .map(log => log.title || log.description || log.notes)
        .filter(Boolean)
        .join('; ')
      const note = record.notes || dayLog || '-'
      const status = record.checkIn ? (record.checkOut ? 'Completed' : 'In progress') : 'No record'
      const statusClass = record.checkIn && record.checkOut ? 'complete' : record.checkIn ? 'pending' : 'neutral'
      const dayName = new Date(`${record.date}T00:00:00`).toLocaleDateString('en-US', { weekday: 'short' })
      return `<tr>
        <td class="date">${escapeHtml(formatDate(record.date))}<span>${escapeHtml(dayName)}</span></td>
        <td>${escapeHtml(formatTime(record.checkIn))}</td>
        <td>${escapeHtml(formatTime(record.checkOut))}</td>
        <td>${escapeHtml(record.workMode || 'Office')}</td>
        <td class="notes">${escapeHtml(note)}</td>
        <td><span class="status ${statusClass}">${escapeHtml(status)}</span></td>
      </tr>`
    }).join('')

  const html = `<!DOCTYPE html>
  <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40">
  <head>
    <meta charset="utf-8" />
    <title>Attendance Report - ${escapeHtml(data.employeeName)} - ${escapeHtml(monthLabel)}</title>
    <!--[if gte mso 9]><xml><w:WordDocument><w:View>Print</w:View><w:Zoom>100</w:Zoom></w:WordDocument></xml><![endif]-->
    <style>
      @page { size: A4; margin: 18mm 16mm 18mm 16mm; }
      * { box-sizing: border-box; }
      body { margin: 0; color: #1f2937; font-family: Arial, Helvetica, sans-serif; font-size: 10pt; line-height: 1.45; }
      .page { width: 100%; }
      .top-rule { height: 7px; background: #f97316; margin-bottom: 20px; }
      .header { width: 100%; border-collapse: collapse; margin-bottom: 22px; }
      .brand { width: 58%; vertical-align: top; }
      .brand-name { margin: 0; color: #111827; font-size: 19pt; font-weight: 700; letter-spacing: -.3pt; }
      .brand-subtitle { margin: 3px 0 0; color: #6b7280; font-size: 8.5pt; letter-spacing: .4pt; text-transform: uppercase; }
      .report-meta { width: 42%; text-align: right; vertical-align: top; }
      .badge { display: inline-block; padding: 4px 10px; color: #047857; background: #ecfdf5; border: 1px solid #a7f3d0; border-radius: 12px; font-size: 8pt; font-weight: 700; letter-spacing: .4pt; }
      .report-title { margin: 10px 0 1px; color: #111827; font-size: 12pt; font-weight: 700; }
      .report-period { margin: 0; color: #6b7280; font-size: 9pt; }
      .section-title { margin: 0 0 9px; color: #6b7280; font-size: 8pt; font-weight: 700; letter-spacing: .8pt; text-transform: uppercase; }
      .employee-card { width: 100%; border-collapse: collapse; margin-bottom: 20px; border: 1px solid #e5e7eb; background: #fafafa; }
      .employee-card td { width: 25%; padding: 11px 12px; vertical-align: top; border-right: 1px solid #e5e7eb; }
      .employee-card td:last-child { border-right: 0; }
      .label { display: block; margin-bottom: 3px; color: #9ca3af; font-size: 7.5pt; font-weight: 700; letter-spacing: .45pt; text-transform: uppercase; }
      .value { color: #111827; font-size: 10pt; font-weight: 700; word-break: break-word; }
      .summary { width: 100%; border-collapse: separate; border-spacing: 8px 0; margin: 0 -8px 22px; }
      .summary td { width: 33.33%; padding: 12px 14px; border: 1px solid #e5e7eb; background: #ffffff; }
      .summary-label { display: block; color: #6b7280; font-size: 8pt; font-weight: 700; letter-spacing: .35pt; text-transform: uppercase; }
      .summary-value { display: block; margin-top: 3px; color: #ea580c; font-size: 17pt; font-weight: 700; }
      .attendance { width: 100%; border-collapse: collapse; table-layout: fixed; }
      .attendance th { padding: 9px 8px; color: #ffffff; background: #1f2937; border: 1px solid #1f2937; font-size: 8pt; font-weight: 700; letter-spacing: .25pt; text-align: left; text-transform: uppercase; }
      .attendance td { padding: 9px 8px; border-bottom: 1px solid #e5e7eb; color: #374151; font-size: 8.5pt; vertical-align: top; word-wrap: break-word; }
      .attendance tr:nth-child(even) td { background: #f9fafb; }
      .attendance th:nth-child(1) { width: 13%; } .attendance th:nth-child(2), .attendance th:nth-child(3) { width: 13%; }
      .attendance th:nth-child(4) { width: 12%; } .attendance th:nth-child(5) { width: 34%; } .attendance th:nth-child(6) { width: 15%; }
      .date { color: #111827; font-weight: 700; } .date span { display: block; margin-top: 1px; color: #9ca3af; font-size: 7.5pt; font-weight: 400; }
      .notes { color: #4b5563; }
      .status { display: inline-block; padding: 3px 6px; border-radius: 9px; font-size: 7.5pt; font-weight: 700; white-space: nowrap; }
      .complete { color: #047857; background: #ecfdf5; } .pending { color: #b45309; background: #fffbeb; } .neutral { color: #6b7280; background: #f3f4f6; }
      .empty { padding: 22px !important; color: #6b7280 !important; text-align: center; }
      .footer { margin-top: 20px; padding-top: 10px; border-top: 1px solid #e5e7eb; color: #9ca3af; font-size: 8pt; }
      .footer td { vertical-align: top; } .footer .right { text-align: right; }
    </style>
  </head>
  <body><div class="page">
    <div class="top-rule"></div>
    <table class="header"><tr><td class="brand"><h1 class="brand-name">Sasha Infinity</h1><p class="brand-subtitle">Human Resources & Attendance Management</p></td><td class="report-meta"><span class="badge">OFFICIAL HR RECORD</span><p class="report-title">Monthly Attendance Report</p><p class="report-period">${escapeHtml(monthLabel)}</p></td></tr></table>
    <p class="section-title">Employee Information</p>
    <table class="employee-card"><tr><td><span class="label">Employee Name</span><span class="value">${escapeHtml(data.employeeName)}</span></td><td><span class="label">Employee ID</span><span class="value">${escapeHtml(data.employeeId)}</span></td><td><span class="label">Department</span><span class="value">${escapeHtml(data.department)}</span></td><td><span class="label">Email Address</span><span class="value">${escapeHtml(data.employeeEmail)}</span></td></tr></table>
    <p class="section-title">Attendance Overview</p>
    <table class="summary"><tr><td><span class="summary-label">Recorded Days</span><span class="summary-value">${attendance.length}</span></td><td><span class="summary-label">Completed Days</span><span class="summary-value">${completedDays}</span></td><td><span class="summary-label">Incomplete Days</span><span class="summary-value">${incompleteDays}</span></td></tr></table>
    <p class="section-title">Attendance Details</p>
    <table class="attendance"><thead><tr><th>Date</th><th>Check In</th><th>Check Out</th><th>Work Mode</th><th>Work Details</th><th>Status</th></tr></thead><tbody>${rows}</tbody></table>
    <table class="footer"><tr><td>This report is system-generated for official HR documentation.</td><td class="right">Generated on ${escapeHtml(new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }))}</td></tr></table>
  </div></body></html>`

  const blob = new Blob(['\ufeff' + html], { type: 'application/msword;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  const shortMonth = monthDate.toLocaleString('en-US', { month: 'short' }) || monthNumber
  link.download = `${data.employeeName.replace(/\s+/g, '_')}_Attendance_Report_${shortMonth}_${year}.doc`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}
